import { cn } from '@april9au/react-common/utils';

import type { ChatImage } from '@/chat/types';
import type { ImageField } from '@prismicio/client';
import { Icon } from '@/components/ui/icon';
import { PrismicMedia } from '@/components/ui/media';
import { useStrings } from '@/i18n/use-strings';
import { ImageSheet, useImageSheet } from './image-sheet';
import { PlainText } from './rich-text';

/**
 * Pictures in the thread. Both messages open the same sheet on tap, and both
 * reserve their space from the image's own dimensions before it loads — a
 * photograph that pops into place shoves the whole conversation down.
 */

/** The aspect ratio Prismic reports, with a 16:9 fallback for an unsized asset. */
function ratioOf(image: ImageField): string {
  const dimensions = image.dimensions;

  return dimensions ? `${dimensions.width} / ${dimensions.height}` : '16 / 9';
}

function Thumbnail({ image, className }: { image: ChatImage; className?: string }) {
  if (image.image.url === null) {
    return <div className={cn('bg-chat-rail size-full', className)} />;
  }

  // The rail tint under the photo, so the reveal is a fade between two
  // surfaces rather than a flash against the bubble.
  return (
    <PrismicMedia
      image={image.image}
      placeholder="var(--chat-rail)"
      className={cn('size-full object-cover', className)}
    />
  );
}

/** The affordance that says a picture opens. */
function OpenBadge() {
  const { chat } = useStrings();

  return (
    <span className="pointer-events-none absolute end-2 bottom-2 flex items-center gap-1 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-semibold text-white">
      <Icon name="passport/expand" className="size-3.5" />
      {chat.chat_image_open}
    </span>
  );
}

export function ImageMessage({ image }: { image: ChatImage }) {
  const sheet = useImageSheet();

  return (
    <>
      <button
        type="button"
        onClick={() => sheet.open(0)}
        className="chat-pressable bg-bubble-buddy focus-visible:ring-surf-blue/40 relative block w-full max-w-[85%] overflow-hidden rounded-2xl rounded-es-md text-start shadow-[0_1px_2px_rgb(16_24_64/0.06),0_4px_12px_-6px_rgb(16_24_64/0.14)] focus-visible:ring-[3px] focus-visible:outline-none"
      >
        <span className="relative block w-full" style={{ aspectRatio: ratioOf(image.image) }}>
          <Thumbnail image={image} />
          <OpenBadge />
        </span>
        <span className="text-chat-ink block px-4 py-3 text-sm font-semibold">
          <PlainText field={image.caption} />
        </span>
      </button>

      <ImageSheet images={[image]} index={sheet.index} onIndexChange={sheet.onIndexChange} />
    </>
  );
}

/**
 * A scroll-snap carousel. No carousel library: one flex row with snap points
 * does the whole job, keeps the keyboard working through native focus, and
 * adds nothing to the bundle.
 */
export function CarouselMessage({ images }: { images: Array<ChatImage> }) {
  const sheet = useImageSheet();
  const { chat } = useStrings();

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <ul
        // The negative margin lets the row bleed to the edge of the phone
        // while the bubbles keep their gutter.
        className="-mx-4 flex snap-x snap-mandatory [scrollbar-width:none] gap-3 overflow-x-auto px-4 pb-1 [&::-webkit-scrollbar]:hidden"
        aria-label={chat.chat_takeaways_heading}
      >
        {images.map((image, index) => (
          <li key={image.image.id ?? index} className="w-[68%] shrink-0 snap-start sm:w-[48%]">
            <button
              type="button"
              onClick={() => sheet.open(index)}
              className="chat-pressable bg-bubble-buddy focus-visible:ring-surf-blue/40 relative block w-full overflow-hidden rounded-2xl text-start shadow-[0_1px_2px_rgb(16_24_64/0.06),0_4px_12px_-6px_rgb(16_24_64/0.14)] focus-visible:ring-[3px] focus-visible:outline-none"
            >
              <span className="block w-full" style={{ aspectRatio: ratioOf(image.image) }}>
                <Thumbnail image={image} />
              </span>
              <span className="text-chat-ink block px-3 py-2.5 text-[13px] font-semibold">
                <PlainText field={image.caption} />
              </span>
            </button>
          </li>
        ))}
      </ul>

      <ImageSheet images={images} index={sheet.index} onIndexChange={sheet.onIndexChange} />
    </>
  );
}
